import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PORTFOLIO, WORK_FILTERS, waLink } from "@/data/site";
import { SectionHeading } from "@/components/pp/Shared";
import { track } from "@/lib/api";

export default function FeaturedWork() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const items = filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === filter);

  return (
    <section id="work" className="relative py-24 md:py-32" data-testid="work">
      <div className="pp-container">
        <SectionHeading overline="Featured Work" title={<>Creative That Moves.<br />Campaigns That Perform.</>} testid="work-heading" />

        <div className="flex flex-wrap gap-2 mb-10">
          {WORK_FILTERS.map((f) => (
            <button key={f} onClick={() => { setFilter(f); track("portfolio_filter", { filter: f }); }}
              data-testid={`work-filter-${f.replace(/\s+/g, '-').toLowerCase()}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-full transition-colors ${
                filter === f ? "bg-[#f59e0b] text-black" : "border border-white/10 text-slate-300 hover:bg-white/5"}`}>
              {f}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <AnimatePresence mode="popLayout">
            {items.map((p) => (
              <motion.button layout key={p.id}
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                onClick={() => { setActive(p); track("portfolio_open", { project: p.id }); }}
                whileHover={{ y: -6 }}
                data-testid={`work-card-${p.id}`}
                className={`group relative overflow-hidden rounded-2xl pp-card min-h-[280px] md:min-h-[340px] text-left ${p.span}`}
                style={{ perspective: 1000 }}
              >
                <img src={p.image} alt={p.title} loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, rgba(3,3,5,0.95))" }} />
                <div className="relative h-full flex flex-col justify-end p-7">
                  <span className="pp-overline mb-2">{p.category}</span>
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-display uppercase text-2xl md:text-3xl text-white leading-tight">{p.title}</h3>
                    <span className="shrink-0 h-11 w-11 rounded-full grid place-items-center bg-[#f59e0b] text-black opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowUpRight size={20} />
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-400">{p.industry} · {p.service}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl bg-[#0a0a0e] border-white/10 text-white p-0 overflow-hidden" data-testid="work-modal">
          {active && (
            <div>
              <DialogTitle className="sr-only">{active.title}</DialogTitle>
              <DialogDescription className="sr-only">{active.goal}</DialogDescription>
              <div className="relative h-56 md:h-72">
                <img src={active.image} alt={active.title} className="absolute inset-0 h-full w-full object-cover opacity-70" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent, rgba(10,10,14,1))" }} />
                <div className="absolute bottom-0 p-8">
                  <span className="pp-overline">{active.category}</span>
                  <h3 className="font-display uppercase text-3xl md:text-4xl mt-2">{active.title}</h3>
                </div>
              </div>
              <div className="p-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div><p className="pp-overline mb-1">Industry</p><p className="text-slate-300">{active.industry}</p></div>
                <div><p className="pp-overline mb-1">Service</p><p className="text-slate-300">{active.service}</p></div>
                <div className="sm:col-span-2"><p className="pp-overline mb-1">Goal</p><p className="text-slate-300">{active.goal}</p></div>
                <div className="sm:col-span-2"><p className="pp-overline mb-1">Outcome</p><p className="text-slate-400 italic">{active.outcome}</p></div>
              </div>
              <div className="px-8 pb-8">
                <a href={waLink(`Hi PixelPapad, I liked the "${active.title}" work. Can we discuss something similar?`)} target="_blank" rel="noopener noreferrer"
                  onClick={() => track("whatsapp_click", { location: "work_modal" })}
                  className="inline-flex items-center gap-2 bg-[#f59e0b] text-black font-semibold uppercase tracking-widest text-xs px-6 py-3.5 hover:bg-white transition-colors">
                  Start a Similar Project
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
